import { useCallback, useEffect, useRef, useState } from 'react';
import {CitationCredit} from './SoftwareAcknowledgements';
import {
  BookOpen,
  GraduationCap,
  HelpCircle,
  ArrowUpRight,
  ChevronsUpDown,
  ArrowLeft,
  Settings,
  Download,
  Menu,
} from 'lucide-react';
import { api, getProfileId, setProfileId } from './api';
import { Modal, Spinner, ErrorNotice } from './components';
import { Home } from './Home';
import { AssignmentReader } from './AssignmentReader';
import { Teacher, Review } from './Teacher';
import type { Bootstrap, Profile } from './types';
import { WorkbenchAccount } from './WorkbenchAccount';
import { usePortalAppearance } from './portal-appearance';
import './portal-design.css';

const homeRoute = () => {
  const path = location.hash.slice(1) || '/learn';
  if (path === '/progress') { history.replaceState(null, '', `${location.pathname}${location.search}#/learn`); return '/learn'; }
  return path;
};

export type Navigate = (path: string) => Promise<void>;
function routeId(value: string) {
  try { return decodeURIComponent(value); } catch { return value; }
}
export function App() {
  const [bootstrap, setBootstrap] = useState<Bootstrap | null>(null);
  const [error, setError] = useState('');
  const [route, setRoute] = useState(homeRoute);
  const [profileId, setProfile] = useState(getProfileId());
  const [profileOpen, setProfileOpen] = useState(false);
  const [help, setHelp] = useState(false);
  const [navigationOpen, setNavigationOpen] = useState(false);
  const [accountSection, setAccountSection] = useState<string | null>(null);
  const appearance = usePortalAppearance();
  const guard = useRef<null | (() => Promise<void>)>(null);
  const load = () => {
    setError('');
    api<Bootstrap>('/bootstrap')
      .then(setBootstrap)
      .catch((cause) => setError(cause.message));
  };
  useEffect(load, []);
  useEffect(() => {
    if (bootstrap && !bootstrap.profiles.some((item) => item.id === profileId)) {
      const id = bootstrap.profiles[0].id;
      setProfileId(id);
      setProfile(id);
    }
  }, [bootstrap, profileId]);
  useEffect(() => {
    const update = () => setRoute(homeRoute());
    window.addEventListener('hashchange', update);
    return () => window.removeEventListener('hashchange', update);
  }, []);
  const registerGuard = useCallback((fn: null | (() => Promise<void>)) => {
    guard.current = fn;
  }, []);
  const navigate = useCallback(async (path: string) => {
    try {
      await guard.current?.();
      location.hash = path;
      setRoute(path);
      window.scrollTo(0, 0);
    } catch {
      /* The workspace exposes the saving error and preserves the draft. */
    }
  }, []);
  async function chooseProfile(profile: Profile) {
    try {
      await guard.current?.();
    } catch {
      setProfileOpen(false);
      return;
    }
    guard.current = null;
    setProfileId(profile.id);
    setProfile(profile.id);
    setProfileOpen(false);
    const next = profile.role === 'teacher' ? '/teacher' : '/learn';
    location.hash = next;
    setRoute(next);
  }
  async function accountHome() {
    try {
      await guard.current?.();
      setAccountSection('profile');
    } catch {
      /* Saved draft errors remain visible. */
    }
  }
  if (!bootstrap)
    return (
      <div className="boot">
        <div className="brand">
          Immerso Gnosis<span>STUDENTS</span>
        </div>
        {error ? <ErrorNotice message={error} retry={load} /> : <Spinner />}
      </div>
    );
  const profile = bootstrap.profiles.find((item) => item.id === profileId) || bootstrap.profiles[0];
  const isTeacher = profile.role === 'teacher';
  const lessonId = route.startsWith('/lesson/') ? route.split('/')[2] : null;
  const assignmentId = route.startsWith('/assignment/') ? route.split('/')[2] : null;
  const reviewId = route.startsWith('/review/') ? route.split('/')[2] : null;
  return (
    <div className={`app-shell portal-design ${lessonId || assignmentId ? 'workspace-shell' : ''}`}>
      {accountSection && <WorkbenchAccount initialSection={accountSection} appearance={appearance} onClose={() => setAccountSection(null)} onSaved={load}/>}
      <a
        className="skip-link"
        href="#main-content"
        onClick={(event) => {
          event.preventDefault();
          document.getElementById('main-content')?.focus();
        }}
      >
        Skip to content
      </a>
      <aside className="sidebar" data-navigation-open={navigationOpen}>
        <button
          className="brand"
          onClick={() => void navigate(isTeacher ? '/teacher' : '/learn')}
          aria-label="Marvin home"
        >
          <img src="/favicon.ico?v=marvin-head-v2" alt="" /><span>Marvin<small>Student portal</small></span>
        </button>
        <button className="portal-mobile-toggle" aria-label="Navigation" aria-expanded={navigationOpen} aria-controls="portal-navigation" onClick={() => setNavigationOpen(value => !value)}><Menu size={20}/><span>Menu</span></button>
        <div className="portal-navigation" id="portal-navigation">
        <nav aria-label="Main navigation">
          {isTeacher ? (
            <button
              aria-label="Teaching space"
              className={`nav-item ${!reviewId ? 'active' : ''}`}
              onClick={() => void navigate('/teacher')}
            >
              <GraduationCap size={20} />
              <span>Teaching space</span>
            </button>
          ) : (
            <>
              <button
                aria-label={lessonId || assignmentId ? 'Home' : 'My studies'}
                className="nav-item"
                onClick={() => void navigate('/learn')}
              >
                <BookOpen size={20} />
                <span>{lessonId || assignmentId ? 'Home' : 'My studies'}</span>
              </button>
            </>
          )}
        </nav>
        <div id="portal-context-nav" />
        <div className="sidebar-bottom">
          <CitationCredit/>
          <button className="nav-item" onClick={() => void accountHome()}><Settings size={19}/><span>Settings</span></button>
          <a className="nav-item" href="/downloads"><Download size={19}/><span>Get the PC app</span></a>
          <button
            className="nav-item help-link"
            aria-label="Help"
            onClick={() => setHelp(true)}
          >
            <HelpCircle size={19} />
            <span>Help</span>
          </button>
          <button
            className="profile-button"
            onClick={() =>
              bootstrap.authMode === 'accounts' ? void accountHome() : setProfileOpen(true)
            }
            aria-label={
              bootstrap.authMode === 'accounts'
                ? 'Student workspace account'
                : 'Switch pilot profile'
            }
          >
            <span className="avatar">{profile.initials}</span>
            <span className="profile-name">
              <strong>{profile.name}</strong>
              <small>
                {bootstrap.authMode === 'accounts'
                  ? 'Student workspace'
                  : `${isTeacher ? 'Teacher' : 'Student'} · Pilot`}
              </small>
            </span>
            <ChevronsUpDown size={15} />
          </button>
        </div>
        </div>
      </aside>
      <div className="main-shell">
        <main
          id="main-content"
          tabIndex={-1}
          key={`${profile.id}:${assignmentId || lessonId || reviewId || route}`}
        >
          {(assignmentId || lessonId) && !isTeacher ? (
            <AssignmentReader id={routeId(assignmentId || lessonId!)} sessionLink={!assignmentId} navigate={navigate} />
          ) : reviewId && isTeacher ? (
            <Review id={reviewId} navigate={navigate} />
          ) : isTeacher ? (
            <Teacher profile={profile} navigate={navigate} />
          ) : (
            <Home
              key={route}
              profile={profile}
              isAccount={bootstrap.authMode === 'accounts'}
              navigate={navigate}
              registerGuard={registerGuard}
              assignmentsView={route === '/assignments'}
            />
          )}
        </main>
      </div>
      {profileOpen && (
        <Modal title="Switch pilot profile" onClose={() => setProfileOpen(false)}>
          <p className="modal-intro">
            Try the student and teacher journeys with these fictional profiles. This development
            switch will be replaced by sign-in.
          </p>
          <div className="profile-options">
            {bootstrap.profiles.map((item) => (
              <button
                key={item.id}
                className={`profile-option ${profile.id === item.id ? 'selected' : ''}`}
                onClick={() => void chooseProfile(item)}
              >
                <span className="avatar">{item.initials}</span>
                <span>
                  <strong>{item.name}</strong>
                  <small>
                    {item.role === 'teacher' ? 'Teacher workspace' : 'Student · Science pilot'}
                  </small>
                </span>
                <ArrowUpRight size={18} />
              </button>
            ))}
          </div>
        </Modal>
      )}
      {help && (
        <Modal title="Using the student portal" onClose={() => setHelp(false)}>
          <div className="help-content">
            <p>Manage your studies, courses and modules, create assignments, and read work already saved to your account.</p>
            <p>Use the Marvin PC app to edit assignments and files, research, use AI assistance, and record your work. These activities are unavailable in the web portal.</p>
            <p>The portal shows the latest account copy. Work still waiting to sync from your PC will appear once it has synced.</p>
          </div>
        </Modal>
      )}
    </div>
  );
}

export function Back({ navigate, teacher = false }: { navigate: Navigate; teacher?: boolean }) {
  return (
    <button className="back-link" onClick={() => void navigate(teacher ? '/teacher' : '/learn')}>
      <ArrowLeft size={16} />
      {teacher ? 'Teaching space' : 'My studies'}
    </button>
  );
}
